import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const read = (): Theme => {
  try { return (localStorage.getItem('ibms.theme') as Theme) ?? 'system'; } catch { return 'system'; }
};

const resolve = (theme: Theme) =>
  theme === 'system'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme;

export function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle('dark', resolve(theme) === 'dark');
  try {
    if (theme === 'system') localStorage.removeItem('ibms.theme');
    else localStorage.setItem('ibms.theme', theme);
  } catch { /* private mode */ }
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(read);

  useEffect(() => { applyTheme(theme); }, [theme]);

  // Follow the OS while set to "system".
  useEffect(() => {
    if (theme !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [theme]);

  return { theme, setTheme, resolved: resolve(theme) as 'light' | 'dark' };
}
